import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useContextUpload } from '~/hooks/useContextUpload'
import { speechToText } from '~/services/speechService'
import type { CanvasContextInfo } from '~/services/chatService'
import type { TaskObjectWithNoDetails } from '~/types/TaskType'
import type { ContextUploadApiItem } from '~/types/contextUpload'
import { BetterToast } from '~/utils/toast'
import { ChatBox } from './ChatBox'
import type { SlashCommandOpenRequest } from './ChatBox'
import type { AgentMode, SpeedMode } from './ModeDropdown'
import { appendCodeReviewTaskId, getChatSlashCommands } from './slashCommands'
import { UploadCardsContainer } from './UploadCardsContainer'

export interface ComposerTaskActionRequest {
  taskId: number
  requestId: number
}

interface ChatComposerProps {
  onSendMessage: (
    message: string,
    executionMode?: string,
    verificationMode?: string,
    contextUpload?: ContextUploadApiItem[],
    canvasContextInfo?: CanvasContextInfo[],
    isSpeechInput?: boolean
  ) => void
  isLoading?: boolean
  placeholder?: string
  agentMode?: AgentMode
  speedMode?: SpeedMode
  onAgentModeChange?: (mode: AgentMode) => void
  onSpeedModeChange?: (mode: SpeedMode) => void
  routingTask?: TaskObjectWithNoDetails | null
  isTaskChatPaused?: boolean
  codeReviewTaskRequest?: ComposerTaskActionRequest | null
  feedbackDraftRequest?: ComposerTaskActionRequest | null
  selectionDraft?: string
}

const DEFAULT_AGENT_MODE: AgentMode = 'agent'
const DEFAULT_SPEED_MODE: SpeedMode = 'verify'

export const ChatComposer: React.FC<ChatComposerProps> = ({
  onSendMessage,
  isLoading = false,
  placeholder = 'Type your message...',
  routingTask,
  isTaskChatPaused = false,
  codeReviewTaskRequest,
  feedbackDraftRequest,
  selectionDraft
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isSpeechInputDraft, setIsSpeechInputDraft] = useState(false)
  const [codeReviewTaskId, setCodeReviewTaskId] = useState<number | null>(null)
  const handledCodeReviewTaskRequestRef = useRef<number | null>(null)
  const handledFeedbackDraftRequestRef = useRef<number | null>(null)
  const lastAppliedSelectionDraftRef = useRef<string | undefined>(undefined)
  const { isSearching } = useSidebarContext()
  const { currentWorkspace } = useWorkspaceContext()

  const {
    contextUploads,
    handleFilesUpload,
    handleFolderUpload,
    handleRemoveContextUpload,
    clearUploads,
    getContextUploadData,
    hasPendingUploads
  } = useContextUpload(currentWorkspace?.uuid)

  useEffect(() => {
    if (!codeReviewTaskRequest) {
      handledCodeReviewTaskRequestRef.current = null
      return
    }

    if (handledCodeReviewTaskRequestRef.current === codeReviewTaskRequest.requestId) {
      return
    }

    handledCodeReviewTaskRequestRef.current = codeReviewTaskRequest.requestId
    setCodeReviewTaskId(codeReviewTaskRequest.taskId)
    setInputValue('/code-review')
    setIsSpeechInputDraft(false)
  }, [codeReviewTaskRequest])

  useEffect(() => {
    if (!feedbackDraftRequest) {
      handledFeedbackDraftRequestRef.current = null
      return
    }

    if (handledFeedbackDraftRequestRef.current === feedbackDraftRequest.requestId) {
      return
    }

    handledFeedbackDraftRequestRef.current = feedbackDraftRequest.requestId
    setCodeReviewTaskId(null)
    setInputValue(`Refinement Feedback for the Coding Task Id ${feedbackDraftRequest.taskId}: `)
    setIsSpeechInputDraft(false)
  }, [feedbackDraftRequest])

  useEffect(() => {
    if (selectionDraft === undefined) return
    if (lastAppliedSelectionDraftRef.current === selectionDraft) return

    lastAppliedSelectionDraftRef.current = selectionDraft
    setCodeReviewTaskId(null)
    setInputValue(selectionDraft)
    setIsSpeechInputDraft(false)
  }, [selectionDraft])

  const slashCommandOpenRequest: SlashCommandOpenRequest | null = codeReviewTaskRequest
    ? {
        commandId: 'code-review',
        requestId: codeReviewTaskRequest.requestId
      }
    : null
  const slashCommands = useMemo(
    () => getChatSlashCommands({ codeReviewTaskId }),
    [codeReviewTaskId]
  )

  const handleSend = (messageOverride?: string) => {
    const message = typeof messageOverride === 'string' ? messageOverride : inputValue
    let trimmedMessage = message.trim()
    if (!trimmedMessage || isLoading) return

    if (
      codeReviewTaskId != null &&
      trimmedMessage.toLowerCase().startsWith('/code-review') &&
      !/\bTaskId\s*:/i.test(trimmedMessage)
    ) {
      trimmedMessage = appendCodeReviewTaskId(trimmedMessage, codeReviewTaskId)
    }

    if (isTaskChatPaused) {
      BetterToast.error(
        routingTask
          ? `Task #${routingTask.task_id} is still running. Wait for it to finish before sending feedback.`
          : 'A task is still running. Wait for it to finish before sending feedback.'
      )
      return
    }

    if (hasPendingUploads) {
      BetterToast.error('Please wait for uploads to complete')
      return
    }

    const contextUploadData = getContextUploadData()
    const completedContextUploadData = contextUploadData.length > 0 ? contextUploadData : undefined

    if (isSpeechInputDraft) {
      onSendMessage(
        trimmedMessage,
        DEFAULT_AGENT_MODE,
        DEFAULT_SPEED_MODE,
        completedContextUploadData,
        undefined,
        true
      )
    } else {
      onSendMessage(
        trimmedMessage,
        DEFAULT_AGENT_MODE,
        DEFAULT_SPEED_MODE,
        completedContextUploadData
      )
    }

    setInputValue('')
    setIsSpeechInputDraft(false)
    setCodeReviewTaskId(null)
    clearUploads()
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    if (!value.trim().toLowerCase().startsWith('/code-review')) {
      setCodeReviewTaskId(null)
    }
    if (!value.trim()) {
      setIsSpeechInputDraft(false)
    }
  }

  const handleTranscribeAudio = async (audio: Blob) => {
    if (!currentWorkspace?.uuid) {
      throw new Error('No workspace selected')
    }
    return speechToText(currentWorkspace.uuid, audio)
  }

  const topContent =
    contextUploads.length > 0 ? (
      <UploadCardsContainer uploads={contextUploads} onRemove={handleRemoveContextUpload} />
    ) : undefined

  return (
    <ChatBox
      value={inputValue}
      onChange={handleInputChange}
      onSend={handleSend}
      placeholder={
        isTaskChatPaused && routingTask ? `Task #${routingTask.task_id} is running...` : placeholder
      }
      disabled={isLoading || isTaskChatPaused}
      multiline={true}
      autoFocus={!isSearching}
      autoResizeInput={false}
      topContent={topContent}
      onFilesSelect={handleFilesUpload}
      onFolderSelect={handleFolderUpload}
      currentUploadCount={contextUploads.length}
      showModeControls={false}
      isSendDisabled={hasPendingUploads}
      rotateDropdownArrowsOnOpen={true}
      bookmarksPopoverAlignment='chat'
      onTranscribeAudio={currentWorkspace?.uuid ? handleTranscribeAudio : undefined}
      onSpeechTranscriptApplied={() => setIsSpeechInputDraft(true)}
      speechButtonTone='light'
      slashCommands={slashCommands}
      slashCommandOpenRequest={slashCommandOpenRequest}
    />
  )
}
